import { useApi } from '@classgo/app-kit';
import type { StudentDetail } from '@classgo/shared';
import { createStudentInputSchema, updateStudentInputSchema } from '@classgo/shared';
import { Input, Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';

import { listQuery } from '../academic/api';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi, peopleQueries } from '../people/api';
import { useTenant } from '../tenant/tenant-provider';
import { FormShell } from './form-shell';

const text = (value: string) => (value.trim() === '' ? undefined : value.trim());

/**
 * Create a student (with the first enrollment when a group is chosen) or edit their identity.
 * Document types come from the school's country pack, never from the app.
 */
export const StudentForm = ({
  current,
  onDone,
}: {
  current: StudentDetail | null;
  onDone: (id: string) => void;
}) => {
  const t = useT();
  const api = useApi();
  const { sede } = useTenant();
  const pack = useQuery(peopleQueries.countryPack(api));
  const years = useQuery(listQuery(api, 'years'));
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const activeYear = (years.data?.items ?? []).find((year) => year.status === 'active');
  const form = useForm({
    schema: current ? updateStudentInputSchema : createStudentInputSchema,
    initial: {
      givenName: current?.givenName ?? '',
      familyName: current?.familyName ?? '',
      documentType: current?.documentType ?? 'TI',
      documentNumber: current?.documentNumber ?? '',
      birthDate: current?.birthDate ?? '',
      email: current?.email ?? '',
      groupId: '',
      enrolledOn: '',
    },
    toInput: (values) =>
      current
        ? {
            givenName: values.givenName,
            familyName: values.familyName,
            documentType: values.documentType,
            documentNumber:
              values.documentNumber.trim() === '' ? null : values.documentNumber.trim(),
            birthDate: values.birthDate === '' ? null : values.birthDate,
            email: values.email.trim() === '' ? null : values.email.trim(),
          }
        : {
            givenName: values.givenName,
            familyName: values.familyName,
            documentType: values.documentType,
            documentNumber: text(values.documentNumber),
            birthDate: text(values.birthDate),
            email: text(values.email),
            sedeId: sede.id,
            enrollment:
              values.groupId && activeYear
                ? {
                    schoolYearId: activeYear.id,
                    groupId: values.groupId,
                    enrolledOn: text(values.enrolledOn),
                  }
                : undefined,
          },
  });
  const groupOptions = (groups.data?.items ?? []).filter(
    (group) => !activeYear || group.schoolYearId === activeYear.id,
  );
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            const ref = current
              ? await peopleApi(api).updateStudent(current.id, input as never)
              : await peopleApi(api).createStudent(input as never);
            onDone(ref.id);
          })
          .then(() => undefined);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('people.field.givenName')}
          required
          value={form.values.givenName}
          onChange={(event) => form.set('givenName', event.target.value)}
          {...(form.fieldError('givenName') ? { error: form.fieldError('givenName') } : {})}
        />
        <Input
          label={t('people.field.familyName')}
          required
          value={form.values.familyName}
          onChange={(event) => form.set('familyName', event.target.value)}
          {...(form.fieldError('familyName') ? { error: form.fieldError('familyName') } : {})}
        />
        <Select
          label={t('people.field.documentType')}
          required
          value={form.values.documentType}
          onChange={(event) => form.set('documentType', event.target.value)}
          options={(
            pack.data?.documentTypes ?? [
              { code: 'TI', name: 'Tarjeta de identidad', forMinors: true },
            ]
          ).map((type) => ({ value: type.code, label: `${type.code} · ${type.name}` }))}
          {...(form.fieldError('documentType') ? { error: form.fieldError('documentType') } : {})}
        />
        <Input
          label={t('people.field.documentNumber')}
          value={form.values.documentNumber}
          onChange={(event) => form.set('documentNumber', event.target.value)}
          {...(form.fieldError('documentNumber')
            ? { error: form.fieldError('documentNumber') }
            : {})}
        />
        <Input
          label={t('people.field.birthDate')}
          type="date"
          value={form.values.birthDate}
          onChange={(event) => form.set('birthDate', event.target.value)}
          {...(form.fieldError('birthDate') ? { error: form.fieldError('birthDate') } : {})}
        />
        <Input
          label={t('people.field.email')}
          type="email"
          help={t('student.field.email.help')}
          value={form.values.email}
          onChange={(event) => form.set('email', event.target.value)}
          {...(form.fieldError('email') ? { error: form.fieldError('email') } : {})}
        />
      </div>
      {!current ? (
        <div className="grid gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2">
          <Select
            label={t('student.enroll.group')}
            help={
              activeYear
                ? t('student.enroll.help', { year: activeYear.name })
                : t('student.enroll.noYear')
            }
            value={form.values.groupId}
            disabled={!activeYear}
            onChange={(event) => form.set('groupId', event.target.value)}
            options={[
              { value: '', label: t('student.enroll.later') },
              ...groupOptions.map((group) => ({ value: group.id, label: group.name })),
            ]}
          />
          <Input
            label={t('student.enroll.enrolledOn')}
            type="date"
            value={form.values.enrolledOn}
            disabled={!form.values.groupId}
            onChange={(event) => form.set('enrolledOn', event.target.value)}
          />
        </div>
      ) : null}
    </FormShell>
  );
};
